import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ban, Eye, EyeOff } from 'lucide-react';
import { useTools } from '../../../../ToolProvider.jsx';
import { useDataModelo3dContext } from '../../../../DataProvider.jsx';
import { PX_PER_MM } from '../../../../../../fabricjs/editor.js';
import { resolveProductType } from '../../../../../../products/registry.js';
import InputSpinner2 from '../InputSpinner2/InputSpinner2.jsx';
import {
    IconLayerBringToFront,
    IconLayerBringForward,
    IconLayerSendBackwards,
    IconLayerSendToBack,
    IconCopy,
    IconTrash,
    IconGroup,
    IconUngroup,
} from '../../../ToolSelectorTop/Icons.jsx';

const TYPE_LABEL = {
    'i-text':   'Texto',
    'text':     'Texto',
    'rect':     'Rect',
    'triangle': 'Triáng',
    'circle':   'Círculo',
    'group':    'Grupo',
    'path':     'Forma',
    'image':    'Imagen',
};

// Mismo criterio que `isHoleObject`/`getObjectCategory` en editor.js: la
// ranura de colgado de la caja de luz se distingue por `class`, no por
// `id` — antes caía en el catch-all de abajo y salía mal etiquetada
// como "Grupo".
const getTypeTag = (obj) => {
    if (obj._vectorizedText) return 'Vector';
    if (obj.class === 'drawing_doesnt_move') return 'Fijo';
    if (obj.class === 'screw') return 'Tornillo';
    if (obj.class === 'crocs') return 'Pin Crocs';
    if (obj.class === 'napkin_joint' || String(obj?.id ?? '').startsWith('napkinjoint_')) return 'Perfil base';
    if (obj._geometryRole === 'hole' || String(obj?.id ?? '').startsWith('hole') || obj.class === 'keyhole') return 'Agujero';
    if (obj.class === 'internal_element' || String(obj?.id ?? '').startsWith('insert_')) {
        if (obj._silbatoId || obj.id?.includes('_silbato_')) return 'Silbato';
        if (obj._abridorId || obj.id?.includes('_abridor_')) return 'Abrebotellas';
        if (obj._abrelatasId || obj.id?.includes('_abrelatas_')) return 'Abrelatas';
        return obj.id?.includes('_nfc_') ? 'NFC' : 'Imán';
    }
    if (String(obj?.id ?? '').startsWith('drawings_')) return 'SVG';
    return TYPE_LABEL[obj.type] ?? 'Objeto';
};

// Categorías del bloque de visibilidad — deben coincidir con
// `getObjectCategory` en editor.js.
const CATEGORIES = [
    { key: 'background', label: 'Fondo' },
    { key: 'holes',      label: 'Agujeros' },
    { key: 'internal',   label: 'Internos' },
    { key: 'fixed',      label: 'Fijo' },
    { key: 'drawings',   label: 'Dibujos' },
];

const getObjectName = (obj) => {
    if (obj.type === 'i-text' || obj.type === 'text') {
        return obj.text ? `"${obj.text.slice(0, 18)}"` : '—';
    }
    if (obj.name) return obj.name;
    return null;
};

const TypeTag = ({ obj, inverted }) => (
    <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 font-medium ${
        inverted ? 'bg-primary-content text-primary' : 'bg-base-300 text-base-content'
    }`}>
        {getTypeTag(obj)}
    </span>
);

// Qué cuenta como agujero, con el mismo criterio que `getTypeTag`: la
// operación "Agujero pasante" (`_geometryRole`), el hueco de colgar del
// llavero y la ranura de la caja de luz.
const isHole = (obj) => obj?._geometryRole === 'hole'
    || obj?.class === 'technical_hole'
    || String(obj?.id ?? '').startsWith('hole')
    || obj?.class === 'keyhole'
    || obj?.class === 'screw';

const mm = (px) => px / PX_PER_MM;

/**
 * Medida de un agujero, en mm de lo que se ve.
 *
 * Se lee del objeto ya escalado (`getScaledWidth`), no de `width`: un círculo
 * arrastrado por una esquina conserva `radius` y cambia `scaleX`, así que el
 * radio "de fábrica" no es el que se va a taladrar. Un círculo escalado a lo
 * ancho deja de ser círculo, y entonces se muestra como caja.
 */
const getHoleSize = (obj) => {
    if (!obj) return null;
    const width = mm(obj.getScaledWidth?.() ?? 0);
    const height = mm(obj.getScaledHeight?.() ?? 0);
    if (!width || !height) return null;
    const isRound = obj.type === 'circle' && Math.abs(width - height) < 0.05;
    if (isRound) {
        const radius = width / 2;
        return {
            label: `r ${radius.toFixed(1)}`,
            title: `Radio ${radius.toFixed(2)} mm · diámetro ${width.toFixed(2)} mm`,
        };
    }
    return {
        label: `${width.toFixed(1)}×${height.toFixed(1)}`,
        title: `Tamaño ${width.toFixed(2)} × ${height.toFixed(2)} mm`,
    };
};

const HoleSize = ({ obj }) => {
    if (!isHole(obj)) return null;
    const size = getHoleSize(obj);
    if (!size) return null;
    return (
        <span
            className="shrink-0 rounded bg-base-300/60 px-1 py-0.5 font-mono text-[10px] leading-none opacity-70"
            title={size.title}
        >
            {size.label}
        </span>
    );
};

const getFillColor = (obj) => {
    if (typeof obj?._vaciadoFill === 'string') return obj._vaciadoFill;
    return typeof obj?.fill === 'string' ? obj.fill : 'transparent';
};

const FillSwatch = ({ obj }) => {
    if (obj?.type === 'group') return <span className="w-3.5 shrink-0" />;
    const fill = getFillColor(obj);
    return (
        <span
            className="w-3.5 h-3 rounded-full border border-base-content/30 shrink-0"
            style={{ backgroundColor: fill }}
            title={`Relleno: ${fill}`}
            aria-label={`Color de relleno ${fill}`}
        />
    );
};

// Toggle de mostrar/ocultar de una fila suelta (capa de primer nivel o hijo
// anidado de un grupo) — independiente del bloque de categorías de arriba,
// ambos actúan sobre el mismo estado (ver setObjectVisible en editor.js).
const VisibilityToggle = ({ obj, onSetObjectVisible }) => {
    const isVisible = obj.visible !== false;
    return (
        <button
            type="button"
            className={`btn btn-xs btn-circle shrink-0 ${isVisible ? 'btn-ghost' : 'btn-primary'}`}
            onClick={(event) => { event.stopPropagation(); onSetObjectVisible(obj, !isVisible); }}
            title={isVisible ? 'Ocultar' : 'Mostrar'}
            aria-pressed={!isVisible}
        >
            {isVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        </button>
    );
};

// «0,75» sin ceros de más y con coma decimal, que es como se escriben los mm
// en el resto del editor.
const formatDepth = (mm) => Number(mm)
    .toFixed(2)
    .replace(/0+$/, '')
    .replace(/\.$/, '')
    .replace('.', ',');

const ReliefField = ({ title, hint, value, hasOwn, min, max, resetLabel, onChange, onReset }) => (
    <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold leading-tight sm:text-[11px]">{title}</p>
        <p className="text-xs leading-tight opacity-70 sm:text-[11px]">{hint}</p>
        <InputSpinner2
            label="Milímetros"
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            initialStep={0.05}
            stepOptions={[0.05, 0.1, 0.25, 0.5]}
            decimals={2}
        />
        <button
            type="button"
            className="btn btn-sm btn-ghost sm:btn-xs"
            disabled={!hasOwn}
            onClick={onReset}
        >
            {resetLabel}
        </button>
    </div>
);

/**
 * Relieve de una capa de dibujo: cuánto entra en el fondo y cuánto sobresale.
 *
 * Sin valor propio la capa hereda del grupo que la contiene, y si tampoco lo
 * tiene, la profundidad cae en el parámetro global de "Parámetros 3D" y el
 * saliente en 0. Por eso el chip enseña «—» cuando no hay nada fijado y los
 * números sólo cuando alguien los ha puesto ahí: así se ve de un vistazo qué
 * capas se salen de lo común.
 *
 * Sólo tiene sentido en el llavero, que es el único producto cuya geometría mete
 * los dibujos dentro del fondo.
 */
// Ancho del desplegable en pantalla ancha, y alto que se le reserva para decidir
// si cabe por debajo del chip o hay que abrirlo hacia arriba.
const RELIEF_POPOVER_WIDTH = 208;
const RELIEF_POPOVER_HEIGHT = 300;

/**
 * Dónde pintar el desplegable, en coordenadas de ventana.
 *
 * En pantalla estrecha es una hoja pegada abajo. En ancha cuelga del chip,
 * alineado a su derecha, y se da la vuelta si no cabe por debajo.
 */
const reliefPopoverStyle = (rect) => {
    const { innerWidth, innerHeight } = window;
    if (innerWidth < 640) {
        return { left: 8, right: 8, bottom: 8, maxHeight: '70vh' };
    }
    const left = Math.max(
        8,
        Math.min(rect.right - RELIEF_POPOVER_WIDTH, innerWidth - RELIEF_POPOVER_WIDTH - 8),
    );
    const fitsBelow = innerHeight - rect.bottom >= RELIEF_POPOVER_HEIGHT;
    return fitsBelow
        ? { left, top: rect.bottom + 4, width: RELIEF_POPOVER_WIDTH }
        : { left, bottom: innerHeight - rect.top + 4, width: RELIEF_POPOVER_WIDTH };
};

const DrawingReliefChip = ({ obj, control }) => {
    const [open, setOpen] = useState(false);
    const [anchor, setAnchor] = useState(null);
    const wrapperRef = useRef(null);
    const popoverRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        // El desplegable se pinta en un portal, así que "fuera" son los dos: ni
        // el chip ni el propio desplegable cuentan como clic fuera.
        const handlePointerDown = (event) => {
            if (wrapperRef.current?.contains(event.target)) return;
            if (popoverRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [open]);

    // La posición se recalcula mientras esté abierto: el panel de capas tiene
    // scroll propio, y la fila se mueve bajo el desplegable si se rueda.
    useEffect(() => {
        if (!open) {
            setAnchor(null);
            return undefined;
        }
        const update = () => {
            const rect = wrapperRef.current?.getBoundingClientRect();
            if (rect) setAnchor(reliefPopoverStyle(rect));
        };
        update();
        window.addEventListener('resize', update);
        // En captura para enterarse también del scroll de los contenedores.
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    if (!control?.enabled || control.getObjectCategory(obj) !== 'drawings') return null;

    const { depth, raise } = control.getObjectDrawingRelief(obj);
    const depthMm = depth.own ?? depth.inherited ?? control.globalDepthMm;
    const raiseMm = raise.own ?? raise.inherited ?? 0;
    const hasOwnDepth = depth.own !== null;
    const hasOwnRaise = raise.own !== null;
    // Sin caras (caja de luz) todo vive en la de delante.
    const face = control.getObjectDrawingFace?.(obj) ?? 'front';
    const onBack = face === 'back';
    const marked = hasOwnDepth || hasOwnRaise || onBack;

    const label = marked
        ? [
            onBack ? '⟲' : null,
            hasOwnDepth ? formatDepth(depth.own) : null,
            hasOwnRaise ? `↑${formatDepth(raise.own)}` : null,
        ].filter(Boolean).join(' ') || '⟲'
        : '—';
    const source = (own, inherited) => (
        own !== null ? 'de esta capa' : (inherited !== null ? 'heredado del grupo' : 'por defecto')
    );

    return (
        <span className="relative shrink-0" ref={wrapperRef}>
            <button
                type="button"
                // Algo más grande en móvil: es un objetivo táctil, y a 10px
                // acertarle con el dedo era cuestión de suerte.
                className={`rounded border px-1.5 py-1 font-mono text-[11px] leading-none sm:px-1 sm:py-0.5 sm:text-[10px] ${
                    marked
                        ? 'border-primary/50 bg-primary/15 text-primary'
                        : 'border-transparent bg-base-300/60 opacity-60'
                }`}
                onClick={(event) => { event.stopPropagation(); setOpen(value => !value); }}
                title={
                    (control.onSetObjectDrawingFace ? `Cara ${onBack ? 'de atrás' : 'de delante'} · ` : '')
                    + `entra ${formatDepth(depthMm)} mm (${source(depth.own, depth.inherited)})`
                    + ` · sobresale ${formatDepth(raiseMm)} mm (${source(raise.own, raise.inherited)})`
                }
            >
                {label}
            </button>

            {open && anchor && createPortal(
                /* Va a `document.body`, no dentro de la fila. El panel de capas
                   tiene scroll propio, así que cualquier cosa anclada a la fila
                   se recortaba contra su borde — que es justo por lo que no se
                   veían las cotas. Desde el body no lo recorta nadie, y la
                   posición se calcula a mano contra el chip. */
                <div
                    ref={popoverRef}
                    style={anchor}
                    className="fixed z-[60] flex max-h-[70vh] flex-col gap-3 overflow-y-auto rounded-lg border border-base-300 bg-base-100 p-3 shadow-xl sm:max-h-none sm:overflow-visible sm:p-2"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold sm:text-xs">Relieve de la capa</span>
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost sm:btn-xs"
                            onClick={() => setOpen(false)}
                        >
                            Cerrar
                        </button>
                    </div>
                    <ReliefField
                        title={control.depthLabel ?? 'Entra en el fondo'}
                        hint={control.depthHint ?? 'A partir del grosor total atraviesa la placa.'}
                        value={depthMm}
                        hasOwn={hasOwnDepth}
                        min={0.1}
                        max={control.maxDepthMm}
                        resetLabel={`Usar la global (${formatDepth(control.globalDepthMm)} mm)`}
                        onChange={(mm) => control.onSetObjectDrawingRelief(obj, 'depth', mm)}
                        onReset={() => control.onSetObjectDrawingRelief(obj, 'depth', null)}
                    />
                    <ReliefField
                        title="Sobresale"
                        hint="Cuánto sale de su cara hacia fuera. 0 la deja al ras."
                        value={raiseMm}
                        hasOwn={hasOwnRaise}
                        min={0}
                        max={control.maxRaiseMm}
                        resetLabel="Dejar al ras"
                        onChange={(mm) => control.onSetObjectDrawingRelief(obj, 'raise', mm)}
                        onReset={() => control.onSetObjectDrawingRelief(obj, 'raise', null)}
                    />

                    {/* Cambiar de cara no mueve la capa de sitio: se espeja sobre
                        su propio centro, porque lo que se lee bien por un lado se
                        lee del revés por el otro. Sólo el llavero tiene dos caras
                        que pintar; el panel de una caja de luz tiene una. */}
                    {control.onSetObjectDrawingFace && (
                    <div className="flex flex-col gap-1">
                        <p className="text-[11px] font-semibold leading-tight">Cara</p>
                        <div className="join w-full">
                            {[
                                { value: 'front', label: 'Delante' },
                                { value: 'back', label: 'Detrás' },
                            ].map(({ value, label: text }) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`btn btn-xs join-item flex-1 ${face === value ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
                                    onClick={() => control.onSetObjectDrawingFace(obj, value)}
                                >
                                    {text}
                                </button>
                            ))}
                        </div>
                    </div>
                    )}
                </div>,
                document.body,
            )}
        </span>
    );
};

const RenderExcludeToggle = ({ obj, onSetObjectRenderExcluded }) => {
    const excluded = Boolean(obj._excludeFromRender);
    return (
        <button
            type="button"
            className={`btn btn-xs btn-circle shrink-0 ${excluded ? 'btn-warning' : 'btn-ghost'}`}
            onClick={(event) => { event.stopPropagation(); onSetObjectRenderExcluded?.(obj, !excluded); }}
            title={excluded ? 'Incluir en operaciones 3D' : 'Excluir de operaciones 3D'}
            aria-pressed={excluded}
        >
            <Ban className="h-3.5 w-3.5" />
        </button>
    );
};

// Capa bloqueada (drawing_doesnt_move): no se puede mover ni arrastrar
const isLocked = (obj) => obj.class === 'drawing_doesnt_move';
const isTouchLikeEvent = (event) => (
    event.pointerType === 'touch'
    || event.pointerType === 'pen'
    || event.nativeEvent?.pointerType === 'touch'
    || event.nativeEvent?.pointerType === 'pen'
);
const isAdditiveSelection = (event, pointerType = null) => (
    event.ctrlKey || event.metaKey || isTouchLikeEvent(event) || pointerType === 'touch' || pointerType === 'pen'
);

const LockIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="currentColor"
        className="w-3.5 h-3.5 shrink-0"
    >
        <path
            fillRule="evenodd"
            d="M8 1a3 3 0 0 0-3 3v2H4.5A1.5 1.5 0 0 0 3 7.5v6A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-6A1.5 1.5 0 0 0 11.5 6H11V4a3 3 0 0 0-3-3Zm1.5 5V4a1.5 1.5 0 0 0-3 0v2h3Z"
            clipRule="evenodd"
        />
    </svg>
);

const ChevronIcon = ({ open }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        fill="currentColor"
        className={`w-3 h-3 shrink-0 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
    >
        <path
            fillRule="evenodd"
            d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L9.19 8 6.22 5.03a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
        />
    </svg>
);

// Recursive: renders a group's children indented. Not draggable.
const GroupChildren = ({ groupObj, rootGroup, depth, selected, expandedGroups, toggleExpand, selectNestedObject, onSetObjectVisible, onSetObjectRenderExcluded, reliefControl, lastPointerTypeRef }) => {
    const children = groupObj._objects ? [...groupObj._objects].reverse() : [];
    if (children.length === 0) return null;

    return (
        <div className="flex flex-col">
            {children.map((child, idx) => {
                const isGroup    = child.type === 'group' && child._objects?.length > 0;
                const isExpanded = expandedGroups.has(child);
                const name       = getObjectName(child);
                const locked     = isLocked(rootGroup) || isLocked(child);
                const isSelected = selected.has(child);

                return (
                    <div key={idx}>
                        <div
                            onPointerDown={(event) => { lastPointerTypeRef.current = event.pointerType; }}
                            onClick={locked ? undefined : (event) => selectNestedObject(child, rootGroup, isAdditiveSelection(event, lastPointerTypeRef.current))}
                            className={`flex items-center gap-1.5 w-full border py-1 rounded text-xs transition-colors cursor-pointer ${
                                isSelected
                                    ? 'border-primary bg-primary/15 text-base-content opacity-100 ring-1 ring-primary/45'
                                    : locked
                                        ? 'border-transparent cursor-not-allowed opacity-50'
                                        : 'border-transparent hover:border-primary/35 hover:bg-base-200 opacity-75'
                            }`}
                            style={{ paddingLeft: `${depth * 12 + 4}px` }}
                        >
                            {/* Línea vertical de indentación */}
                            <span className="w-px self-stretch bg-base-300 shrink-0 mr-0.5" />

                            {/* Chevron si es grupo con hijos */}
                            {isGroup ? (
                                <button
                                    onClick={(e) => toggleExpand(child, e)}
                                    className="shrink-0 opacity-60 hover:opacity-100 p-0.5 rounded"
                                >
                                    <ChevronIcon open={isExpanded} />
                                </button>
                            ) : (
                                <span className="w-4 shrink-0" />
                            )}

                            <TypeTag obj={child} inverted={false} />

                            <FillSwatch obj={child} />

                            <HoleSize obj={child} />

                            {name && (
                                <span className="truncate flex-1 opacity-70">{name}</span>
                            )}

                            {/* Los controles, siempre pegados al borde derecho. El
                                `ml-auto` los empuja aunque la capa no tenga nombre
                                (la mayoría de los dibujos no lo tienen) — sin él se
                                quedaban a media fila y el desplegable del relieve
                                salía donde no se podía ver. */}
                            <span className="ml-auto flex shrink-0 items-center gap-1">
                                <DrawingReliefChip obj={child} control={reliefControl} />
                                <RenderExcludeToggle obj={child} onSetObjectRenderExcluded={onSetObjectRenderExcluded} />
                                <VisibilityToggle obj={child} onSetObjectVisible={onSetObjectVisible} />
                            </span>
                        </div>

                        {/* Hijos anidados */}
                        {isGroup && isExpanded && (
                            <GroupChildren
                                groupObj={child}
                                rootGroup={rootGroup}
                                depth={depth + 1}
                                selected={selected}
                                expandedGroups={expandedGroups}
                                toggleExpand={toggleExpand}
                                selectNestedObject={selectNestedObject}
                                onSetObjectVisible={onSetObjectVisible}
                                onSetObjectRenderExcluded={onSetObjectRenderExcluded}
                                reliefControl={reliefControl}
                                lastPointerTypeRef={lastPointerTypeRef}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const LayerPanel = () => {
    const {
        onBringToFront, onBringForward, onSendBackwards, onSendToBack,
        onCopy, onDeleteSelectedObject, onGroupObjects, onUngroupObjects,
        canvas, getLayerSelectedObjects, selectObject, selectNestedObject, reorderObject,
        onSetObjectVisible, onSetObjectRenderExcluded, onSetCategoryVisible, onSoloCategory, onShowAllObjects, getObjectCategory,
        onSetObjectDrawingRelief, getObjectDrawingRelief,
        getObjectDrawingFace, onSetObjectDrawingFace, getEditingFace, onSetFaceVisible,
    } = useTools();
    const dataModelo3d = useDataModelo3dContext();

    // El relieve por capa existe en los dos productos, pero mide cosas
    // distintas y por eso los topes y el texto salen de aquí:
    //
    // - **Llavero**: el dibujo se mete dentro de una placa maciza. Entrando el
    //   grosor entero lo atraviesa; saliendo, sobresale por encima.
    // - **Caja de luz**: la profundidad se mide desde la cara del arte hacia
    //   la luz. Por defecto es la banda del arte (panel menos difusor); más
    //   hondo se come el difusor y al máximo — el grosor del panel — el color
    //   es lo único que queda y hace de ventana; menos hondo deja capa opaca
    //   detrás y ese color se apaga.
    //
    // La cara (delante/detrás) sigue siendo sólo del llavero: un panel de caja
    // de luz tiene una única cara de arte.
    const productType = resolveProductType(dataModelo3d);
    const isLightbox = productType === 'lightbox';
    const thicknessMm = Math.max(Number(dataModelo3d?.thiknessModelo3d) || 6, 0.1);
    const frontThicknessMm = Math.max(Number(dataModelo3d?.frontThicknessMm) || 2, 0.1);
    // El suelo de la profundidad es la lámina que va bajo los dibujos, no la
    // del fondo: por defecto el dibujo se apoya justo encima de la suya.
    const artDiffuserMm = Math.max(Number(dataModelo3d?.artDiffuserThicknessMm) || 0, 0);
    const artBandMm = Math.max(frontThicknessMm - artDiffuserMm, 0.1);
    const reliefControl = {
        enabled: productType === 'keyring' || isLightbox,
        globalDepthMm: isLightbox ? artBandMm : (dataModelo3d?.drawingDepthMm ?? 0.75),
        maxDepthMm: isLightbox ? frontThicknessMm : thicknessMm,
        maxRaiseMm: isLightbox ? frontThicknessMm * 2 : thicknessMm,
        depthLabel: isLightbox ? 'Entra hacia la luz' : 'Entra en el fondo',
        depthHint: isLightbox
            ? 'Desde la cara del arte hacia dentro. Por defecto el dibujo se apoya en su lámina translúcida; más hondo se la come y al máximo atraviesa el panel y hace de ventana.'
            : null,
        getObjectCategory,
        getObjectDrawingRelief,
        onSetObjectDrawingRelief,
        // Dar la vuelta a una capa sólo tiene sentido en el llavero.
        getObjectDrawingFace: isLightbox ? null : getObjectDrawingFace,
        onSetObjectDrawingFace: isLightbox ? null : onSetObjectDrawingFace,
    };

    const [layerState, setLayerState]   = useState({ objs: [], selected: new Set() });
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const dragFromRef = useRef(null);
    const pointerDragRef = useRef({ active: false, from: null, to: null });
    const lastPointerTypeRef = useRef(null);
    const rowRefs = useRef([]);
    const [dropIndex, setDropIndex]     = useState(null);

    useEffect(() => {
        if (!canvas) return;
        const refresh = () => {
            const all    = canvas.getObjects().filter(obj => !String(obj?.id ?? '').startsWith('background'));
            const active = getLayerSelectedObjects();
            setLayerState({ objs: [...all].reverse(), selected: new Set(active) });
        };
        refresh();
        const events = [
            'selection:created', 'selection:updated', 'selection:cleared',
            'layer:reordered', 'layer:nested-selection', 'layer:changed', 'object:added', 'object:removed',
            'layer:visibility-changed', 'face:changed',
        ];
        events.forEach(e => canvas.on(e, refresh));
        return () => events.forEach(e => canvas.off(e, refresh));
    }, [canvas, getLayerSelectedObjects]);

    // Las capas se agrupan por cara en cuanto hay algo dibujado por detrás:
    // mezcladas en una sola lista no hay forma de saber qué se está mirando. La
    // cara que se está editando va primero, que es la que se puede tocar.
    // Sin nada por detrás la lista se queda exactamente como siempre.
    const rowFace = (obj) => (getObjectCategory?.(obj) === 'drawings' ? getObjectDrawingFace(obj) : 'front');
    const editingFace = getEditingFace?.() ?? 'front';
    const groupByFace = reliefControl.enabled && layerState.objs.some(obj => rowFace(obj) === 'back');
    const objs = groupByFace
        ? [
            ...layerState.objs.filter(obj => rowFace(obj) === editingFace),
            ...layerState.objs.filter(obj => rowFace(obj) !== editingFace),
        ]
        : layerState.objs;
    const { selected } = layerState;

    const FACE_LABEL = { front: 'Cara de delante', back: 'Cara de detrás' };

    const toggleExpand = (obj, e) => {
        e.stopPropagation();
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(obj) ? next.delete(obj) : next.add(obj);
            return next;
        });
    };

    const handleDragStart = (e, visualIdx) => {
        dragFromRef.current = visualIdx;
        e.dataTransfer.effectAllowed = 'move';
        const ghost = document.createElement('div');
        ghost.style.cssText = 'position:fixed;top:-999px';
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 0, 0);
        setTimeout(() => document.body.removeChild(ghost), 0);
    };

    const handleDragOver = (e, visualIdx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropIndex(visualIdx);
    };

    const commitReorder = (fromVisual, toVisualIdx) => {
        dragFromRef.current = null;
        setDropIndex(null);
        if (fromVisual === null || fromVisual === toVisualIdx) return;
        const fromObj  = objs[fromVisual];
        const toObj    = objs[toVisualIdx];
        if (!fromObj || !toObj || !reorderObject) return;
        // No mover capas bloqueadas ni soltar por debajo de ellas
        if (isLocked(fromObj) || isLocked(toObj)) return;
        // Arrastrar entre caras cambiaría el orden Z sin cambiar de cara, así
        // que la fila volvería a saltar a su grupo. Para cambiar de cara está
        // el chip de relieve.
        if (rowFace(fromObj) !== rowFace(toObj)) return;
        const toCanvasIdx = canvas.getObjects().indexOf(toObj);
        reorderObject(fromObj, toCanvasIdx);
    };

    const handleDrop = (e, toVisualIdx) => {
        e.preventDefault();
        commitReorder(dragFromRef.current, toVisualIdx);
    };

    const handleDragEnd = () => {
        dragFromRef.current = null;
        pointerDragRef.current = { active: false, from: null, to: null };
        setDropIndex(null);
    };

    const getVisualIndexFromPoint = (clientY) => {
        let nearest = null;
        let nearestDistance = Infinity;

        rowRefs.current.forEach((row, idx) => {
            if (!row) return;
            const rect = row.getBoundingClientRect();
            const centerY = rect.top + rect.height / 2;
            const distance = Math.abs(clientY - centerY);
            if (distance < nearestDistance) {
                nearest = idx;
                nearestDistance = distance;
            }
        });

        return nearest;
    };

    const handlePointerDragStart = (event, visualIdx) => {
        if (event.pointerType === 'mouse') return;
        const obj = objs[visualIdx];
        if (!obj || isLocked(obj)) return;

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragFromRef.current = visualIdx;
        pointerDragRef.current = { active: true, from: visualIdx, to: visualIdx };
        setDropIndex(visualIdx);
    };

    const handlePointerDragMove = (event) => {
        const drag = pointerDragRef.current;
        if (!drag.active) return;

        event.preventDefault();
        event.stopPropagation();
        const toVisualIdx = getVisualIndexFromPoint(event.clientY);
        const toObj = objs[toVisualIdx];
        if (toVisualIdx === null || !toObj || isLocked(toObj)) return;

        pointerDragRef.current = { ...drag, to: toVisualIdx };
        setDropIndex(toVisualIdx);
    };

    const handlePointerDragEnd = (event) => {
        const drag = pointerDragRef.current;
        if (!drag.active) return;

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        pointerDragRef.current = { active: false, from: null, to: null };
        commitReorder(drag.from, drag.to);
    };

    // Recuento por categoría para el bloque de visibilidad — a propósito
    // usa `canvas.getObjects()` directo (no `objs`, que ya excluye el
    // fondo) porque el fondo también tiene su propia fila de categoría.
    const allCanvasObjects = canvas?.getObjects() ?? [];
    const categoryStats = CATEGORIES.map(({ key, label }) => {
        const categoryObjs = allCanvasObjects.filter(obj => getObjectCategory?.(obj) === key);
        return {
            key,
            label,
            count: categoryObjs.length,
            allVisible: categoryObjs.every(obj => obj.visible !== false),
        };
    });
    const totalCount = allCanvasObjects.length;
    const totalVisible = allCanvasObjects.filter(obj => obj.visible !== false).length;

    const actionButtons = [
        { onClick: onCopy,                icon: <IconCopy />,                      tip: 'Duplicar selección' },
        { onClick: onDeleteSelectedObject, icon: <IconTrash className="w-5 h-5" />, tip: 'Borrar selección' },
        { onClick: onGroupObjects,         icon: <IconGroup className="w-7 h-7" />, tip: 'Agrupar' },
        { onClick: onUngroupObjects,       icon: <IconUngroup className="w-7 h-7"/>, tip: 'Desagrupar' },
    ];

    const layerButtons = [
        { onClick: onBringToFront,  icon: <IconLayerBringToFront />,  tip: 'Traer al frente' },
        { onClick: onBringForward,  icon: <IconLayerBringForward />,  tip: 'Subir capa' },
        { onClick: onSendBackwards, icon: <IconLayerSendBackwards />, tip: 'Bajar capa' },
        { onClick: onSendToBack,    icon: <IconLayerSendToBack />,    tip: 'Enviar al fondo' },
    ];

    return (
        <div className="flex flex-col gap-2 select-none">

            {/* Toolbar */}
            <div className="flex flex-wrap gap-1 justify-center border-b pb-2">
                {actionButtons.map(({ onClick, icon, tip }) => (
                    <button key={tip} onClick={onClick}
                        className="btn btn-sm btn-ghost p-1.5 tooltip tooltip-bottom" data-tip={tip}>
                        {icon}
                    </button>
                ))}
                <div className="w-px bg-base-300 self-stretch mx-0.5" />
                {layerButtons.map(({ onClick, icon, tip }) => (
                    <button key={tip} onClick={onClick}
                        className="btn btn-sm btn-ghost p-1.5 tooltip tooltip-bottom" data-tip={tip}>
                        {icon}
                    </button>
                ))}
            </div>

            {/* Visibilidad por categoría — ayuda de edición, no afecta al
                pedido ni se guarda con el diseño (ver editor.js). */}
            <div className="flex flex-col gap-1 border-b pb-2">
                <div className="flex items-center justify-between px-1">
                    <span className="text-xs opacity-40">{totalVisible}/{totalCount} visibles</span>
                    <button
                        type="button"
                        className="btn btn-xs btn-ghost border border-base-300"
                        onClick={() => onShowAllObjects?.()}
                        disabled={totalVisible === totalCount}
                    >
                        Mostrar todas
                    </button>
                </div>
                {categoryStats.map(({ key, label, count, allVisible }) => (
                    <div
                        key={key}
                        className={`flex items-center gap-1.5 px-1 py-1 rounded text-xs ${count === 0 ? 'opacity-30' : ''}`}
                    >
                        <span className="flex-1 truncate">
                            {label} <span className="opacity-50">({count})</span>
                        </span>
                        <button
                            type="button"
                            className="btn btn-xs btn-ghost"
                            onClick={() => onSoloCategory?.(key)}
                            disabled={count === 0}
                            title={`Ver solo ${label}`}
                        >
                            Solo
                        </button>
                        <button
                            type="button"
                            className={`btn btn-xs btn-circle ${allVisible ? 'btn-ghost' : 'btn-primary'}`}
                            onClick={() => onSetCategoryVisible?.(key, !allVisible)}
                            disabled={count === 0}
                            title={allVisible ? `Ocultar ${label}` : `Mostrar ${label}`}
                            aria-pressed={!allVisible}
                        >
                            {allVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                    </div>
                ))}
            </div>

            {/* Lista de capas */}
            <div className="flex flex-col">
                <span className="text-xs opacity-40 px-1 pb-1">Capas · arriba → abajo</span>

                {objs.length === 0 && (
                    <span className="text-xs opacity-40 px-2 py-2">Sin objetos en el lienzo</span>
                )}

                {objs.map((obj, idx) => {
                    const isSelected  = selected.has(obj);
                    const isDragSrc   = dragFromRef.current === idx;
                    const isDropAbove = dropIndex === idx;
                    const name        = getObjectName(obj);
                    const isGroup     = obj.type === 'group' && obj._objects?.length > 0;
                    const isExpanded  = expandedGroups.has(obj);
                    const locked      = isLocked(obj);
                    const hidden      = obj.visible === false;

                    // Cabecera al empezar cada cara. La de la cara que no se
                    // está editando avisa de que esas capas están fuera de
                    // alcance, que es lo que se ve en el lienzo atenuado.
                    const face = rowFace(obj);
                    const startsFace = groupByFace && (idx === 0 || rowFace(objs[idx - 1]) !== face);
                    const isEditingThisFace = face === editingFace;

                    return (
                        <div key={idx}>
                            {startsFace && (() => {
                                const faceObjs = objs.filter(other => rowFace(other) === face);
                                const faceAllVisible = faceObjs.every(other => other.visible !== false);
                                return (
                                    <div className={`flex items-center gap-1.5 px-1 pt-2 pb-1 ${idx === 0 ? '' : 'mt-1 border-t border-base-300'}`}>
                                        <span className={`text-xs font-semibold ${isEditingThisFace ? '' : 'opacity-50'}`}>
                                            {FACE_LABEL[face]}
                                        </span>
                                        <span className="text-xs opacity-40">({faceObjs.length})</span>
                                        {!isEditingThisFace && (
                                            <span className="text-[10px] opacity-40 truncate">· da la vuelta para editarlas</span>
                                        )}
                                        <button
                                            type="button"
                                            className={`btn btn-xs btn-circle ml-auto shrink-0 ${faceAllVisible ? 'btn-ghost' : 'btn-primary'}`}
                                            onClick={() => onSetFaceVisible?.(face, !faceAllVisible)}
                                            title={faceAllVisible
                                                ? `Ocultar la ${FACE_LABEL[face].toLowerCase()}`
                                                : `Mostrar la ${FACE_LABEL[face].toLowerCase()}`}
                                            aria-pressed={!faceAllVisible}
                                        >
                                            {faceAllVisible
                                                ? <Eye className="h-3.5 w-3.5" />
                                                : <EyeOff className="h-3.5 w-3.5" />}
                                        </button>
                                    </div>
                                );
                            })()}

                            {isDropAbove && !locked && (
                                <div className="h-0.5 bg-primary rounded mx-1 -mb-0.5" />
                            )}

                            {/* Fila principal */}
                            <div
                                ref={(el) => { rowRefs.current[idx] = el; }}
                                draggable={!locked}
                                onDragStart={locked ? undefined : (e) => handleDragStart(e, idx)}
                                onDragOver={locked ? undefined : (e) => handleDragOver(e, idx)}
                                onDrop={locked ? undefined : (e) => handleDrop(e, idx)}
                                onDragEnd={locked ? undefined : handleDragEnd}
                                onPointerDown={(event) => { lastPointerTypeRef.current = event.pointerType; }}
                                onClick={locked ? undefined : (event) => selectObject(obj, isAdditiveSelection(event, lastPointerTypeRef.current))}
                                title={locked ? 'Capa fija — no se puede mover' : undefined}
                                className={`
                                    flex items-center gap-1.5 w-full border px-1 py-1.5 rounded text-sm
                                    transition-colors
                                    ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                                    ${isSelected
                                        ? 'border-primary bg-primary/15 text-base-content ring-1 ring-primary/45'
                                        : locked
                                            ? 'border-transparent'
                                            : 'border-transparent hover:border-primary/35 hover:bg-base-200'}
                                    ${isDragSrc  ? 'opacity-40' : ''}
                                    ${hidden ? 'opacity-40' : ''}
                                `}
                            >
                                {/* Handle de arrastre / candado si está bloqueada */}
                                {locked ? (
                                    <span className="shrink-0 opacity-50 px-0.5" title="Capa fija">
                                        <LockIcon />
                                    </span>
                                ) : (
                                    <span
                                        className="cursor-grab active:cursor-grabbing shrink-0 opacity-30 hover:opacity-60 text-base leading-none px-0.5"
                                        style={{ touchAction: 'none' }}
                                        onPointerDown={(event) => handlePointerDragStart(event, idx)}
                                        onPointerMove={handlePointerDragMove}
                                        onPointerUp={handlePointerDragEnd}
                                        onPointerCancel={handleDragEnd}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        ⠿
                                    </span>
                                )}

                                {/* Número */}
                                <span className={`text-xs w-4 shrink-0 text-right tabular-nums ${isSelected ? 'opacity-70' : 'opacity-40'}`}>
                                    {objs.length - idx}
                                </span>

                                {/* Chevron para grupos */}
                                {isGroup ? (
                                    <button
                                        onClick={(e) => toggleExpand(obj, e)}
                                        className={`shrink-0 p-0.5 rounded hover:bg-black/10 ${isSelected ? 'text-primary-content' : ''}`}
                                    >
                                        <ChevronIcon open={isExpanded} />
                                    </button>
                                ) : (
                                    <span className="w-4 shrink-0" />
                                )}

                                <TypeTag obj={obj} inverted={isSelected} />

                                <FillSwatch obj={obj} />

                                <HoleSize obj={obj} />

                                {name && (
                                    <span className="truncate flex-1 opacity-80 text-xs">{name}</span>
                                )}

                                {/* Mismo criterio que en las filas anidadas: los
                                    controles van siempre al borde derecho. */}
                                <span className="ml-auto flex shrink-0 items-center gap-1">
                                    <DrawingReliefChip obj={obj} control={reliefControl} />
                                    <RenderExcludeToggle obj={obj} onSetObjectRenderExcluded={onSetObjectRenderExcluded} />
                                    <VisibilityToggle obj={obj} onSetObjectVisible={onSetObjectVisible} />
                                </span>
                            </div>

                            {/* Hijos del grupo expandido */}
                            {isGroup && isExpanded && (
                                <GroupChildren
                                    groupObj={obj}
                                    rootGroup={obj}
                                    depth={1}
                                    selected={selected}
                                    expandedGroups={expandedGroups}
                                    toggleExpand={toggleExpand}
                                    onSetObjectVisible={onSetObjectVisible}
                                    onSetObjectRenderExcluded={onSetObjectRenderExcluded}
                                    reliefControl={reliefControl}
                                    lastPointerTypeRef={lastPointerTypeRef}
                                    selectNestedObject={selectNestedObject}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LayerPanel;
